<template>
    <Transition name="slide-up">
        <div v-if="showUpdatePrompt" class="update-prompt">
            <div class="update-prompt-content">
                <div class="update-icon">
                    <Icon type="ios-cloud-download" />
                </div>
                <div class="update-info">
                    <h4 class="update-title">發現新版本</h4>
                    <p class="update-message">系統已更新，建議重新整理以獲得最佳體驗</p>
                </div>
                <div class="update-actions">
                    <Button type="primary" size="small" @click="handleUpdate" :loading="updating">
                        立即更新
                    </Button>
                    <Button size="small" @click="handleLater">
                        稍後再說
                    </Button>
                </div>
                <div class="close-btn" @click="handleLater">
                    <Icon type="ios-close" />
                </div>
            </div>
        </div>
    </Transition>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { eventBus } from '@shared/stores/globalState';

export default {
    name: 'UpdatePrompt',
    setup() {
        const showUpdatePrompt = ref(false);
        const updating = ref(false);
        let updateSWCallback = null;

        // 處理 Service Worker 更新事件（prompt 模式）
        const handleSWUpdate = (updateFn) => {
            console.log('[UpdatePrompt] 收到 SW 更新通知');
            updateSWCallback = updateFn;
            showUpdatePrompt.value = true;
        };

        // 處理 Service Worker 已更新事件（autoUpdate 模式）
        const handleSWUpdated = () => {
            console.log('[UpdatePrompt] SW 已自動更新完成');
            // autoUpdate 模式下 SW 已經更新，提示用戶刷新即可使用新版本
            updateSWCallback = null;
            showUpdatePrompt.value = true;
        };

        // 執行更新
        const handleUpdate = async () => {
            updating.value = true;
            try {
                if (updateSWCallback) {
                    // 調用 Service Worker 更新回調
                    await updateSWCallback(true);
                } else {
                    // 如果沒有回調，直接重新整理頁面
                    window.location.reload();
                }
            } catch (error) {
                console.error('[UpdatePrompt] 更新失敗:', error);
                // 發生錯誤時也嘗試重新整理
                window.location.reload();
            }
        };

        // 稍後再說
        const handleLater = () => {
            showUpdatePrompt.value = false;
            // 設定 5 分鐘後再次提醒
            setTimeout(() => {
                if (updateSWCallback) {
                    showUpdatePrompt.value = true;
                }
            }, 5 * 60 * 1000);
        };

        onMounted(() => {
            // 監聽 Service Worker 更新事件
            eventBus.on('sw:needRefresh', handleSWUpdate);
            eventBus.on('sw:updated', handleSWUpdated);
        });

        onUnmounted(() => {
            eventBus.off('sw:needRefresh', handleSWUpdate);
            eventBus.off('sw:updated', handleSWUpdated);
        });

        return {
            showUpdatePrompt,
            updating,
            handleUpdate,
            handleLater
        };
    }
};
</script>

<style scoped lang="stylus">
.update-prompt
    position: fixed
    bottom: 24px
    right: 24px
    z-index: 10000
    max-width: 400px
    width: calc(100% - 48px)

    @media (max-width: 768px)
        bottom: 16px
        right: 16px
        left: 16px
        width: auto
        max-width: none

.update-prompt-content
    display: flex
    align-items: center
    gap: 12px
    padding: 16px
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%)
    border-radius: 12px
    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.35)
    color: #fff
    position: relative

    @media (max-width: 768px)
        flex-wrap: wrap
        padding: 14px

.update-icon
    flex-shrink: 0
    width: 48px
    height: 48px
    display: flex
    align-items: center
    justify-content: center
    background: rgba(255, 255, 255, 0.2)
    border-radius: 12px

    .ivu-icon
        font-size: 28px
        color: #fff

    @media (max-width: 768px)
        width: 40px
        height: 40px

        .ivu-icon
            font-size: 24px

.update-info
    flex: 1
    min-width: 0

    @media (max-width: 768px)
        flex-basis: calc(100% - 60px)

.update-title
    font-size: 16px
    font-weight: 600
    margin: 0 0 4px 0
    color: #fff

    @media (max-width: 768px)
        font-size: 14px

.update-message
    font-size: 13px
    margin: 0
    color: rgba(255, 255, 255, 0.9)
    line-height: 1.4

    @media (max-width: 768px)
        font-size: 12px

.update-actions
    display: flex
    gap: 8px
    flex-shrink: 0

    @media (max-width: 768px)
        width: 100%
        margin-top: 12px
        justify-content: flex-end

    :deep(.ivu-btn)
        border-radius: 8px
        font-weight: 500

    :deep(.ivu-btn-primary)
        background: #fff !important
        border-color: #fff !important
        color: #667eea !important

        span
            color: #667eea !important

        &:hover
            background: rgba(255, 255, 255, 0.9) !important
            border-color: rgba(255, 255, 255, 0.9) !important

    :deep(.ivu-btn-default)
        background: transparent
        border-color: rgba(255, 255, 255, 0.5)
        color: #fff

        &:hover
            background: rgba(255, 255, 255, 0.1)
            border-color: rgba(255, 255, 255, 0.8)

.close-btn
    position: absolute
    top: -8px
    right: -8px
    width: 24px
    height: 24px
    display: flex
    align-items: center
    justify-content: center
    background: #fff
    border-radius: 50%
    cursor: pointer
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15)
    transition: transform 0.2s

    .ivu-icon
        font-size: 18px
        color: #666

    &:hover
        transform: scale(1.1)

        .ivu-icon
            color: #333

// 動畫效果
.slide-up-enter-active
    animation: slideUp 0.4s ease-out

.slide-up-leave-active
    animation: slideUp 0.3s ease-in reverse

@keyframes slideUp
    from
        opacity: 0
        transform: translateY(100%) scale(0.9)
    to
        opacity: 1
        transform: translateY(0) scale(1)
</style>
